import { sendAllMessageStrings } from 'constants/strings';
import { CardTypeMessagePicturesInlineCarousel } from 'pages/CardTypeMessage/CardTypeMessagePicturesInlineCarousel';
import { CardTypeMessagePreview } from 'pages/CardTypeMessage/CardTypeMessagePreview';
import { ECardTypeTemplateId } from 'types/cardTypeMessage';
import { normalizeCardTypePayload } from 'utils/cardTypeMessage';
import {
  getRichMessagePreviewImageUrl,
  TPreviewMessageAsset,
} from 'utils/messagePreview';

const assetPreviewStrings = sendAllMessageStrings.assetPreview;

export type TAssetBubbleKind = 'richMessage' | 'cardTypeMessage';

/**
 * カードが横並びで届くか（Picturesテンプレート、または複数枚）。
 * 横並びのときは吹き出しを幅いっぱいに広げないと、カードが枠から溢れる。
 */
export function isCardCarouselAsset(
  asset: TPreviewMessageAsset | undefined,
): boolean {
  const parsed = asset ? normalizeCardTypePayload(asset.payload) : null;
  if (!parsed) return false;

  return (
    parsed.templateId === ECardTypeTemplateId.Pictures || parsed.cards.length > 1
  );
}

type Props = {
  kind: TAssetBubbleKind;
  asset?: TPreviewMessageAsset;
  /** 素材を解決できないときに出す文言。 */
  fallbackLabel?: string;
};

/** リッチメッセージ・カードタイプメッセージを、実際に届く見た目で描く。 */
export function AssetBubble({ kind, asset, fallbackLabel }: Props) {
  if (!asset) {
    return (
      <div
        className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-3 py-4 text-center
       text-xs text-neutral-400"
      >
        {fallbackLabel ?? assetPreviewStrings.selectAsset}
      </div>
    );
  }

  if (kind === 'richMessage') {
    const imageUrl = getRichMessagePreviewImageUrl(asset);

    if (!imageUrl) {
      return (
        <div className="rounded-xl border border-neutral-200 bg-white px-3 py-4 text-center text-xs text-neutral-500">
          <p className="font-medium text-neutral-700">{asset.name}</p>
          <p className="mt-1">{assetPreviewStrings.noPreviewImage}</p>
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={asset.name}
        className="block w-full max-w-full rounded-lg border border-neutral-200 bg-white object-contain"
      />
    );
  }

  const parsed = normalizeCardTypePayload(asset.payload);
  if (!parsed?.cards.length) {
    return (
      <div
        className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-3 py-4 text-center 
      text-xs text-neutral-400"
      >
        {assetPreviewStrings.noCardsConfigured}
      </div>
    );
  }

  const templateId = parsed.templateId ?? ECardTypeTemplateId.Products;

  if (templateId === ECardTypeTemplateId.Pictures) {
    return <CardTypeMessagePicturesInlineCarousel cards={parsed.cards} />;
  }

  return (
    // カードはshrink-0なので、min-w-0を通さないと親の幅を無視して横へ溢れる
    <div
      data-testid="asset-bubble-card-scroll"
      className="flex min-w-0 gap-2 overflow-x-auto pb-1"
    >
      {parsed.cards.map((card, index) => (
        <CardTypeMessagePreview
          key={`${index}-${card.header ?? card.name ?? ''}`}
          templateId={templateId}
          card={card}
          variant="inline"
        />
      ))}
    </div>
  );
}
